// state/logout-hygiene.ts — single source of truth for what "Esci" clears.
//
// Bug B3 (cleanup/d-sweep): logout used to clear only the auth token (drawer)
// or the token + 4 hand-picked keys (ProfileScreen). Everything else —
// guided/read lesson progress, the AI-Act disclosure acknowledgment, Simula
// resume state, Allena/Lezione session snapshots — survived across students
// on a shared device. That is both a privacy leak (the next student inherits
// the previous student's study history) and a first-visit-routing leak (the
// onboarding tour / AI disclosure never re-fire for the new account).
//
// Both logout call sites (ProfileScreen "Esci" + LeftDrawer footer "Esci")
// now route through clearStudentLocalState(). When a new student-scoped
// localStorage key ships, add it to STUDENT_DATA_KEYS here — do not add
// ad-hoc removeItem calls at the call sites.
//
// Deliberately NOT cleared (device-level, not student-level):
//   asc_theme_v1            — theme + a11y prefs belong to the device
//   asc_qwen_voice_trace_v1 — device-local voice debug toggle
//   asc_staff_auth_v1       — staff session, separate auth domain (/staff)
//   asc_onboarding_tour_seen_v1 — the tour teaches the UI, which is
//     device-level knowledge: replaying it on every logout→login was the
//     founder's #1 pilot-test annoyance (2026-07-07). A genuinely new student
//     on a shared device sees a slightly stale-free app, which is the lesser
//     cost; the AI-Act disclosure (asc_ai_disclosure_v1) stays per-account.
// The student token key asc_token_v1 is cleared, but via the existing
// setToken(null) helper so the single owner of that key stays api/client.ts.

import { setToken } from '../api/client';
import { useChat } from './chat';
import { useActiveProfFigure } from './active-prof-figure';

/**
 * Every student data key the app persists. Order is
 * alphabetical-ish by surface; the exact order does not matter — the list
 * being COMPLETE does.
 */
export const STUDENT_DATA_KEYS: readonly string[] = [
  'asc_profile_v1',                 // state/profile.ts — name, exam date
  'asc_chat_v1',                    // state/chat.ts — Il Prof chat history
  'asc_simula_resume_v1',           // state/simula-resume-storage.ts — in-flight Simula snapshot
  'asc_simula_seen_v1',             // state/simula-resume-storage.ts — Simula intro seen flag
  'asc_ai_disclosure_v1',           // components/AIActDisclosureModal.tsx — AI-Act ack (EU AI Act Art 13)
  'asc_guided_lesson_progress_v1',  // state/guided-lesson-progress.ts — guided (Fast) lesson progress
  'asc_lesson_read_progress_v1',    // state/lesson-read-progress.ts — read-lesson progress
  'asc_voice_note_hint_v1',         // screens/ProfScreen.tsx — voice-note hint dismissed flag
  'asc_allena_session_v1',          // legacy key — practice-session.ts retired in V29 S8; still cleared for old persisted data
  'asc_lezione_session_v1',         // state/lezione-session.ts — Lezione session snapshot
];

/** Last owner validated by the authenticated /api/me response. */
export const STUDENT_OWNER_KEY = 'asc_student_owner_v1';

/** Complete explicit-logout inventory: student data plus its server-validated owner. */
export const STUDENT_LOCAL_KEYS: readonly string[] = [
  ...STUDENT_DATA_KEYS,
  STUDENT_OWNER_KEY,
];

export type StudentOwnerReconciliationResult =
  | { status: 'ready'; reloadRequired: false }
  | { status: 'reload_required'; reloadRequired: true }
  | { status: 'failure'; reloadRequired: false };

const OWNER_READY: StudentOwnerReconciliationResult = { status: 'ready', reloadRequired: false };
const OWNER_RELOAD: StudentOwnerReconciliationResult = { status: 'reload_required', reloadRequired: true };
const OWNER_FAILURE: StudentOwnerReconciliationResult = { status: 'failure', reloadRequired: false };

/**
 * Bind local student data to a server-validated owner without changing auth.
 * Any unverifiable privacy cleanup fails closed for the future AuthGate.
 */
export function reconcileStudentOwner(validatedStudentId: string): StudentOwnerReconciliationResult {
  if (!validatedStudentId.trim()) return OWNER_FAILURE;

  try {
    const rawOwner = localStorage.getItem(STUDENT_OWNER_KEY);
    let storedOwner: string | null = null;
    if (rawOwner !== null) {
      try {
        const parsed = JSON.parse(rawOwner) as unknown;
        if (typeof parsed === 'string' && parsed.trim()) storedOwner = parsed;
      } catch {
        // Corrupt owner state is handled like an unknown legacy owner below.
      }
    }

    if (storedOwner === validatedStudentId) return OWNER_READY;

    const knownDifferentOwner = storedOwner !== null;
    let hasStudentData = false;
    for (const key of STUDENT_DATA_KEYS) {
      if (localStorage.getItem(key) !== null) hasStudentData = true;
    }

    if (knownDifferentOwner || hasStudentData) {
      for (const key of STUDENT_DATA_KEYS) localStorage.removeItem(key);
      for (const key of STUDENT_DATA_KEYS) {
        if (localStorage.getItem(key) !== null) return OWNER_FAILURE;
      }
    }

    const serializedOwner = JSON.stringify(validatedStudentId);
    localStorage.setItem(STUDENT_OWNER_KEY, serializedOwner);
    if (localStorage.getItem(STUDENT_OWNER_KEY) !== serializedOwner) return OWNER_FAILURE;

    return knownDifferentOwner || hasStudentData ? OWNER_RELOAD : OWNER_READY;
  } catch {
    return OWNER_FAILURE;
  }
}

/**
 * Full student logout hygiene: clears the auth token (via the api/client
 * owner of asc_token_v1) plus every student-scoped localStorage key, so the
 * next student on a shared device starts from a genuinely blank slate.
 *
 * Navigation (full page reload so AuthGate re-evaluates) stays at the call
 * sites — this function only clears state.
 */
export function clearStudentLocalState(): void {
  setToken(null);
  useChat.getState().clearProfFigures();
  useActiveProfFigure.getState().clearSelection();
  for (const key of STUDENT_LOCAL_KEYS) {
    try {
      localStorage.removeItem(key);
    } catch {
      /* non-fatal — storage disabled / quota; never block logout */
    }
  }
}
